define(['./config.js', './util.js', './formatting.js', './validation/index.js', './extend.js', './config-evaluator.js', './validation-state.js'],
    function(config, util, formatting, validation, extend, ConfigEvaluator, ValidationState) {
        'use strict';

        var eventTypes = [
            'activated',
            'cancel',
            'click',
            'dataLoaded',
            'deactivated',
            'destroyed',
            'editItem',
            'labelChanged',
            'labelsChanged',
            'modelChanged',
            'modelPropertyChanged',
            'modelPropertyReset',
            'modelReset',
            'parameterChanged',
            'parametersChanged',
            'save',
            'takeAction',
            'valueChanged',
            'validationReferenceChanged',
            'validationStateChanged'
        ];

        // Special Events are handled as regular events even if isActive == false
        var specialEvents = /^(activated|deactivated|destroyed)/;

        function ComponentInstance(modulePath, instanceRegistry) {
            this.isActive = false;
            this.modulePath = modulePath;
            this.instanceRegistry = instanceRegistry;

            this.events = {};
            this.listeners = [];

            this.model = {};
            this.parameters = {};

            this.labelsCache = {};

            this.isDestroyed = false;

            this.eventPathPrevValue = {
                parameterChanged: {},
                modelPropertyChanged: {},
                modelPropertyChanging: {},
                modelPropertyReset: {}
            };

            this.unregisterFunctions = [];

            this.resetValidationState();

            var self = this;
            util.each(eventTypes, function(type) {
                self.events[type] = [];
            });
        }

        ComponentInstance.prototype.hasParent = function hasParent() {
            return this.getModulePath().indexOf('.') >= 0;
        };

        ComponentInstance.prototype.getOwnInstanceId = function getOwnInstanceId() {
            var split = this.getModulePath().split('.');
            return split[split.length - 1];
        };

        ComponentInstance.prototype.resetValidationState = function resetValidationState() {
            this.validationState = new ValidationState(this);
        };

        ComponentInstance.prototype.getValidationState = function getValidationState() {
            return this.validationState;
        };

        ComponentInstance.prototype.resetModel = function resetModel() {
            this.model = {};
            this.prevModel = null;
            this.trigger('modelReset');
        };

        ComponentInstance.prototype.destroy = function destroy() {
            var self = this;

            this.trigger('destroyed');
            this.events = {};
            util.each(this.listeners, function(listener) {
                if (!listener.instance.isDestroyed && listener.instance != self) {
                    listener.instance.off(listener.eventName, listener.callback);
                }
            });

            util.each(this.unregisterFunctions, function(f) {
                f();
            });

            this.listeners.length = 0;
            this.isDestroyed = true;
        };

        /**
         * Adds a listener for eventName.
         * @param eventName
         * @param callback
         * @returns {Function} - Returns a function that can be called with no arguments to unregister the listener.
         */
        ComponentInstance.prototype.on = function on(eventName, callback) {
            var self = this;
            var split;
            if ((split = eventName.split(':')).length === 2 && this.eventPathPrevValue[split[0]]) {
                if (!this.eventPathPrevValue[split[0]][split[1]]) {
                    this.eventPathPrevValue[split[0]][split[1]] = {
                        prevValue: null,
                        getValue: util.parse(split[1])
                    };
                }

                if (!this.events.hasOwnProperty(eventName)) {
                    this.events[eventName] = [];
                }
            }

            //if event is not a standard asterix event
            if (!this.events.hasOwnProperty(eventName)) {
                var allowedEvents = morningstar.asterix.meta.get(this.getModuleType(), 'allowedEvents');
                //if event is defined as allowed-event in component settings
                if (allowedEvents && allowedEvents.indexOf(eventName) !== -1) {
                    this.events[eventName] = [];
                } else {
                    throw new Error('(' + this.modulePath + ') Unrecognized event: ' + eventName);
                }
            }

            this.events[eventName].push(callback);
            return function unregister() {
                self.off(eventName, callback);
            };
        };

        ComponentInstance.prototype.off = function off(eventName, callback) {
            if (!this.events.hasOwnProperty(eventName)) {
                // already off'd
                return;
            }

            if (this.events.hasOwnProperty(eventName) && this.events[eventName].indexOf(callback) >= 0) {
                this.events[eventName].splice(this.events[eventName].indexOf(callback), 1);
            }
        };

        ComponentInstance.prototype.trigger = function trigger(eventName, args) {
            var self = this;
            var orCb = {
                or: function(callback) {
                    if (!self.events[eventName] || self.events[eventName].length === 0 || !self.isActive) {
                        callback();
                    }
                }
            };

            // Special Events are handled as regular events even if isActive == false
            var isSpecialEvent = specialEvents.test(eventName);

            if (util.isUndefined(args)) {
                args = [];
            }

            if (!this.events[eventName] && !isSpecialEvent) {
                return orCb;
            }

            if (self.isActive || isSpecialEvent) {
                util.each(this.events[eventName], function(callback) {
                    callback.apply(self, args);
                });
            }

            return orCb;
        };

        ComponentInstance.prototype.hasListener = function hasListener(eventName) {
            return this.events.hasOwnProperty(eventName) && this.events[eventName].length > 0;
        };

        ComponentInstance.prototype.listen = function listen(instanceToListenTo, eventName, callback) {
            var listener = util.any(this.listeners, function(obj) {
                return obj.instance === instanceToListenTo && obj.eventName === eventName;
            });
            if (!listener) {
                this.listeners.push({
                    instance: instanceToListenTo,
                    eventName: eventName,
                    callback: callback
                });
                instanceToListenTo.on(eventName, callback);
            }
        };

        ComponentInstance.prototype.getModulePath = function getModulePath() {
            return this.modulePath;
        };

        ComponentInstance.prototype.getModuleType = function getModuleType() {
            return config.getComponentType(this.getModulePath());
        };

        ComponentInstance.prototype.hasParent = function hasParent() {
            return this.getModulePath().indexOf('.') >= 0;
        };

        ComponentInstance.prototype.getOwnInstanceId = function getOwnInstanceId() {
            var split = this.getModulePath().split('.');
            return split[split.length - 1];
        };

        ComponentInstance.prototype.getValue = function getValue() {
            return this.value;
        };

        ComponentInstance.prototype.setValue = function setValue(value) {
            this.value = value;
            this.trigger('valueChanged', [value]);
        };

        ComponentInstance.prototype.getModel = function getModel() {
            return this.model;
        };

        ComponentInstance.prototype.setModel = function setModel(model) {
            if (util.isEqual(model, this.prevModel)) {
                return;
            }

            this.prevModel = util.isObject(model) ? extend(true, util.isArray(model) ? [] : {}, model) : model;
            this._triggerPathEvents('modelPropertyChanging', model);
            this.model = model;
            this.trigger('modelChanged', [model]);
            this._triggerPathEvents('modelPropertyChanged', model);
        };

        ComponentInstance.prototype.setModelProperty = function setModelProperty(propertyName, value) {
            var func = util.parse(propertyName);

            if (value !== null && util.isEqual(func(this.prevModel), value)) {
                return;
            }

            this.prevModel = this.prevModel || {};
            func.assign(this.prevModel, util.isObject(value) ? extend(true, util.isArray(value) ? [] : {}, value) : value);
            func.assign(this.model, value);
            this._triggerPathEvents('modelPropertyChanging', this.model, propertyName);

            this.trigger('modelChanged', [this.model]);
            this.trigger('modelPropertyChanged', [value, propertyName]);
            this._triggerPathEvents('modelPropertyChanged', this.model, propertyName);

            if (value === null) {
                this.trigger('modelPropertyReset', [value, propertyName]);
                this._triggerPathEvents('modelPropertyReset', this.model, propertyName);
            }
        };

        ComponentInstance.prototype.getModelProperty = function getModelProperty(propertyName) {
            return util.parse(propertyName)(this.model);
        };

        ComponentInstance.prototype.validate = function validate(value) {
            this.validator = validation.getValidator(this._getEvaluatedValidationRulesAndListenForChanges());
            if (!this.validator) {
                return;
            }

            var validationState = this.validator.validate(value);
            return validationState;
        };

        ComponentInstance.prototype._isValid = function _isValid(validationState) {
            return !util.any(validationState, function(v) {
                return !v.returnValue;
            });
        };

        ComponentInstance.prototype._triggerPathEvents = function _triggerPathEvents(eventTypeName, fullObj, matchPrefix) {
            var self = this;
            if (matchPrefix && matchPrefix.indexOf('.') !== -1) {
                matchPrefix = matchPrefix.substring(0, matchPrefix.indexOf('.'));
            }

            util.each(this.eventPathPrevValue[eventTypeName], function(v, path) {
                if (matchPrefix && path.indexOf(matchPrefix) !== 0) {
                    return;
                }

                var newValue = v.getValue(fullObj);

                if (!util.isEqual(newValue, v.prevValue)) {
                    if (util.isObject(newValue)) {
                        v.prevValue = extend(true, util.isArray(newValue) ? [] : {}, newValue);
                    } else {
                        v.prevValue = newValue;
                    }

                    self.trigger(eventTypeName + ':' + path, [newValue]);
                } else {
                }
            });
        };

        ComponentInstance.prototype._getEvaluatedValidationRulesAndListenForChanges = function _getEvaluatedValidationRulesAndListenForChanges() {
            var self = this;
            var validation = extend(true, {}, this.getSettings()._validation);
            var evaluator = new ConfigEvaluator(this.modulePath, self.instanceRegistry);
            var result = evaluator.evaluate(validation);
            util.each(result.references, function(reference) {
                self.listen(reference.instance, reference.eventType, function(val) {
                    self.trigger('validationReferenceChanged', [reference, val]);
                });
            });

            return result.evaluated;
        };

        ComponentInstance.prototype.getSettings = function getSettings() {
            var result = config.getSettings(this.modulePath);
            if (result.fields) {
                result.fields = util.field.applyDefinitionsAndAliasesToMany(result.fields, result.fieldDefinitions, result.formatAliases);
            }

            return result;
        };

        ComponentInstance.prototype.getLabels = function getLabels() {
            // if we've already evaluated and cached labels for this languageId, return the cached version
            if (this.labelsCache[this.getSettings().languageId]) {
                return this.labelsCache[this.getSettings().languageId];
            }

            return this.labelsCache[this.getSettings().languageId] = this._getEvaluatedLabelsAndListenForChanges();
        };

        ComponentInstance.prototype._getEvaluatedLabelsAndListenForChanges = function _getEvaluatedLabelsAndListenForChanges() {
            var self = this;
            var labels = config.getLabels(this.modulePath, this.getSettings().languageId);
            var evaluator = new ConfigEvaluator(this.modulePath, this.instanceRegistry);
            var result = evaluator.evaluate(util.merge({}, labels), {
                model: true,
                value: true,
                settings: true,
                parameters: true
            });
            if (!this.isListeningToLabelReferences) {
                this.isListeningToLabelReferences = true;
                util.each(result.references, function(reference) {
                    self.unregisterFunctions.push(reference.listen(function() {
                        var cached = self.labelsCache[self.getSettings().languageId];
                        evaluator = new ConfigEvaluator(self.modulePath, self.instanceRegistry);
                        var nodeRes = evaluator.evaluate(reference.source.expression, {
                            model: true,
                            value: true,
                            settings: true,
                            parameters: true
                        });
                        util.parse(reference.source.path).assign(cached, nodeRes.evaluated);
                        self.trigger('labelChanged', [nodeRes.evaluated, reference.source.path]);
                    }));
                });
            }

            return result.evaluated;
        };

        ComponentInstance.prototype.getFormatter = function getFormatter() {
            var format, labels, currencyId, formatAliases;

            format = config.getSetting(this.modulePath, 'format');
            formatAliases = config.getSetting(this.modulePath, 'formatAliases');
            labels = config.getLabels(this.modulePath, this.getSettings().languageId);
            currencyId = config.getSetting(this.modulePath, 'currencyId');

            var abbreviations = {
                thousands: labels.thousands,
                millions: labels.millions,
                billions: labels.billions,
                trillions: labels.trillions
            };

            // todo(slind): could add language-dependent nullString

            return formatting.getFormatter(this.getSettings().languageId, format, formatAliases, abbreviations, currencyId);
        };

        ComponentInstance.prototype._cloneParameters = function _cloneParameters(parameters) {
            return util.isObject(parameters) ?
                extend(true, util.isArray(parameters) ? [] : {}, parameters, function mergeCustomizer(a, b, key) {
                    if (util.isFunction(a) && !b) {
                        return a;
                    }

                    if (util.isFunction(b)) {
                        return b;
                    }
                }) : parameters;
        };

        ComponentInstance.prototype.setParameters = function setParameters(parameters) {
            if (util.isEqual(parameters, this.prevParameters)) {
                return;
            }

            this.parameters = parameters;
            this.prevParameters = this._cloneParameters(parameters);

            this.trigger('parametersChanged', [this.parameters]);
            this._triggerPathEvents('parameterChanged', parameters);
        };

        ComponentInstance.prototype.getParameters = function getParameters() {
            return this.parameters;
        };

        ComponentInstance.prototype.setParameter = function setParameter(propertyName, value) {
            var func = util.parse(propertyName);
            if (util.isEqual(func(this.prevParameters), value)) {
                return;
            }

            func.assign(this.parameters, value);
            this.prevParameters = this._cloneParameters(this.parameters);
            this.trigger('parameterChanged', [value, propertyName]);
            this.trigger('parameterChanged:' + propertyName, [value]);
        };

        ComponentInstance.prototype.getParameter = function getParameter(propertyName) {
            return util.parse(propertyName)(this.parameters);
        };

        ComponentInstance.prototype.getDefaultColorPalette = function getDefaultColorPalette() {
            return this.getSettings().colors || this.getSettings().colorPalettes[this.getSettings().colorPaletteName];
        };

        ComponentInstance.prototype.activate = function activate() {
            this.isActive = true;
            this.trigger('activated');
        };

        ComponentInstance.prototype.deactivate = function deactivate() {
            this.isActive = false;
            this.trigger('deactivated');
        };

        /**
         * Deprecated! Use getRelative instead ("findRelative" is inconsistent with what it actually does...)
         * @param relativePath
         * @returns {*}
         */
        ComponentInstance.prototype.findRelative = function findRelative(relativePath) {
            return this.instanceRegistry.get(this.getModulePath(), relativePath);
        };

        ComponentInstance.prototype.getRelative = function getRelative(relativePath) {
            return this.instanceRegistry.get(this.getModulePath(), relativePath);
        };

        return ComponentInstance;
    });



/*****************
 ** WEBPACK FOOTER
 ** ./components/core/src/js/component-instance.js
 ** module id = 119
 ** module chunks = 0
 **/